import { useState } from "react"
import { UserPlus } from "lucide-react"
import type { User } from "@mc/types"
import { lookupUserByUsername } from "../../api/users.js"
import { Button } from "../ui/Button.js"
import { Input } from "../ui/Input.js"

interface InviteMemberProps {
  onInvite: (user: User) => Promise<void>
  /** e.g. "usuario" so the button reads "Agregar usuario". */
  actionLabel?: string
}

/**
 * Username -> userId resolution step shared by ServerAccessPage (invite a
 * member) and FamilyDetailPage (invite a family member): both underlying
 * endpoints take a userId, this is the only place that turns a typed
 * username into one (via GET /api/v1/users/lookup, api/users.ts).
 */
export function InviteMember({ onInvite, actionLabel = "Agregar" }: InviteMemberProps) {
  const [username, setUsername] = useState("")
  const [status, setStatus] = useState<"idle" | "checking" | "not-found">("idle")
  const [inviting, setInviting] = useState(false)

  const handleInvite = async () => {
    if (!username) return
    setStatus("checking")
    const { user } = await lookupUserByUsername(username)
    if (!user) {
      setStatus("not-found")
      return
    }
    setStatus("idle")
    setInviting(true)
    try {
      await onInvite(user)
      setUsername("")
    } finally {
      setInviting(false)
    }
  }

  return (
    <div style={{ display: "flex", alignItems: "flex-end", gap: "var(--space-2)", flexWrap: "wrap" }}>
      <div style={{ width: 220 }}>
        <Input
          label="Nombre de usuario"
          value={username}
          onChange={(e) => {
            setUsername(e.target.value)
            setStatus("idle")
          }}
          error={status === "not-found" ? "No encontramos a ese usuario." : undefined}
        />
      </div>
      <Button
        size="md"
        variant="secondary"
        loading={status === "checking" || inviting}
        disabled={!username}
        onClick={handleInvite}
      >
        <UserPlus size={14} aria-hidden />
        {actionLabel}
      </Button>
    </div>
  )
}
